import ConditionalToolTip from "components/design/conditionalToolTip";
import { Button } from "components/DesignSystem/Button/Button";
import { AICategorised } from "components/icons/AICategorised";
import { AICategorisedAccepted } from "components/icons/AICategorisedAccepted";
import { AICategorisedManually } from "components/icons/AICategorisedManually";
import { useToast } from "hooks/useToast";
import { MouseEvent } from "react";
import { useConfirmDocumentTagsMutation } from "store/apis/documentTagReview";
import { DocumentTag, TAG_STATUS } from "types/Models/documentTagReview";
import { BackendError } from "types/utils/error";

type DocumentTagStatusProps = {
  groupId: string;
  documentId: string;
  tagStatus: number | null | undefined;
  tags: DocumentTag[];
  onConfirmed?: () => void;
};

const STATUS_ICON = {
  [TAG_STATUS.PENDING_REVIEW]: () => <AICategorised size="24" />,
  [TAG_STATUS.AI_VERIFIED]: () => <AICategorisedAccepted size="24" />,
  [TAG_STATUS.MANUALLY_ADDED]: () => <AICategorisedManually size="24" />,
};

const STATUS_TOOLTIP_TEXT = {
  [TAG_STATUS.PENDING_REVIEW]: "Tagged by AI Agent, Click to confirm",
  [TAG_STATUS.AI_VERIFIED]: "Manually Confirmed",
  [TAG_STATUS.MANUALLY_ADDED]: "Manually added",
};

export const DocumentTagStatus = ({
  groupId,
  documentId,
  tagStatus,
  tags,
  onConfirmed,
}: DocumentTagStatusProps) => {
  const { alertToast } = useToast();
  const [confirmDocumentTags, { isLoading }] = useConfirmDocumentTagsMutation();

  if (tagStatus === null || tagStatus === undefined) {
    return null;
  }

  const StatusIcon = STATUS_ICON[tagStatus as keyof typeof STATUS_ICON];
  const isPendingReview = tagStatus === TAG_STATUS.PENDING_REVIEW;

  if (!StatusIcon) {
    return null;
  }

  const onConfirm = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    try {
      await confirmDocumentTags({
        groupId,
        documents: [
          { document_id: documentId, tag_ids: tags.map((tag) => tag.uuid) },
        ],
      }).unwrap();
      onConfirmed?.();
    } catch (err) {
      alertToast({
        message:
          (err as BackendError)?.data?.error?.message ?? "Something went wrong",
      });
    }
  };

  const tooltipText =
    STATUS_TOOLTIP_TEXT[tagStatus as keyof typeof STATUS_TOOLTIP_TEXT];

  return (
    <ConditionalToolTip condition={tooltipText} side="right">
      <span>
        <Button
          size="xsmall"
          customType="ghost_icon"
          onClick={(e) => {
            if (isPendingReview && !isLoading) {
              onConfirm(e);
            }
          }}
          isLoading={isLoading}
        >
          <StatusIcon />
        </Button>
      </span>
    </ConditionalToolTip>
  );
};
